// Project Management Constants
// Centralized project management constants

export const PROJECT_PLAN_GUIDE_TEXT = 'Project plan guide: For PT or campaigns with media buy, link the budget utilisation tracker. For other projects, link the signed contract or PO.';

export const LEAD_SOURCE_OPTIONS = [
    { value: 'Ariba', label: 'Ariba' },
    { value: 'Campaign360', label: 'Campaign360' },
    { value: "Daniel's Network", label: "Daniel's Network" },
    { value: "Dom's Network", label: "Dom's Network" },
    { value: 'Email', label: 'Email' },
    { value: 'client', label: 'Existing client' },
    { value: 'GEbiz', label: 'GEbiz' },
    { value: 'Referral', label: 'Referral' },
    { value: 'SEM', label: 'SEM' },
    { value: 'SEO', label: 'SEO' },
    { value: 'Sesami', label: 'Sesami' },
    { value: 'Social', label: 'Social' },
    { value: 'TenderBoard', label: 'TenderBoard' },
    { value: 'website', label: 'Website' },
    { value: 'other', label: 'Other' }
];

// Project status options (if needed in future)
export const PROJECT_STATUSES = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    COMPLETED: 'completed',
    ON_HOLD: 'on_hold'
};

// Project types (if needed in future)
export const PROJECT_TYPES = {
    LEAD: 'LEAD',
    CONFIRMED: 'CONFIRMED',
    PROSPECT: 'PROSPECT'
};

// Lost Project Form Constants
export const PROPOSAL_TYPE_OPTIONS = [
    { value: 'GSLIDE+MOCKUP', label: 'Yes, Customised GSlide proposal with mockup' },
    { value: 'GSLIDE', label: 'Yes, Customised GSlide proposal without mockup' },
    { value: 'CREDENTIAL_DECK', label: 'Yes, Credential deck' },
    { value: 'QUOTATION', label: 'Yes, Quotation' },
    { value: 'NO_PROPOSAL', label: 'No, I did not send any proposal' }
];

export const PROPOSAL_PRESENTATION_OPTIONS = [
    { value: 'FACE_TO_FACE', label: 'Yes, Face to Face' },
    { value: 'ONLINE_WITH_CAMERA', label: 'Yes, Online Call with Camera' },
    { value: 'ONLINE_WITHOUT_CAMERA', label: 'Yes, Online Call without Camera' },
    { value: 'NO_PRESENTATION', label: 'No, I did not present it' }
];

export const WITH_DECISION_MAKER_OPTIONS = [
    { value: 'YES', label: 'Yes' },
    { value: 'NO', label: 'No' },
    { value: 'IDK', label: "I don't know" }
];

// Lead status data is now fetched from database via API endpoints
// No hardcoded constants needed - use project.project_lead_status.name from API response

// Financial Item Code Constants (matching backend financial-constants.js)
// These determine which line items are agency/vendor work (editable) vs media/3rd party (info-only)

// Agency Item Codes (editable items - agency revenue generating work)
export const AGENCY_ITEM_CODES = [
    // Account & Agency Fees
    { code: "account agency fees", category: "Account Management" },
    { code: "incentive mgmt-agency fee(mc)", category: "Account Management" },
    { code: "strategy", category: "Account Management" },

    // Media Services
    { code: "media agency fees", category: "Media" },

    // Creative & Production
    { code: "creative production", category: "Creative" },
    { code: "content", category: "Creative" },
    { code: "photo", category: "Creative" },
    { code: "videography", category: "Creative" },
    { code: "edm", category: "Creative" },

    // Digital/Tech Services
    { code: "web development", category: "Technology" },
    { code: "mobile app", category: "Technology" },
    { code: "hosting", category: "Technology" },
    { code: "maintenance", category: "Technology" },

    // UX/UI Services
    { code: "uxui design", category: "UXUI" },
    { code: "uxui audit & analysis", category: "UXUI" },

    // SEO Services
    { code: "seo", category: "SEO" },

    // Strategic Services
    { code: "research", category: "Insights and Strategy" },

    // Data Services
    { code: "d&a services", category: "Data & Analytics" },

    // Other Services
    { code: "social", category: "General" },
    { code: "training", category: "General" },
    { code: "3rd party", category: "General" },  // Can be both included and excluded
    { code: "discount", category: "General" }    // Special: always treated as revenue
];

// Vendor Item Codes (info-only items - media buys, 3rd party costs)
export const VENDOR_ITEM_CODES = [
    { code: "3rd party", category: "Media" },  // When used for media buying
    { code: "media buy", category: "Media" },
    { code: "tracking", category: "Media" },
    { code: "incentive management-pt", category: "General" },
    { code: "platform fee (mc)", category: "General" },
    { code: "boost platform fee 1.62%", category: "General" }
];

// Function to determine if item code represents agency/vendor work (editable)
export const isAgencyVendorItem = (itemCode) => {
    const code = (itemCode || '').toLowerCase().trim();
    if (!code) return true; // Default to editable if no code

    // Check if it's explicitly a vendor item code
    const isVendor = VENDOR_ITEM_CODES.some(vendorItem => {
        const vendorCode = vendorItem.code.toLowerCase();
        return code.includes(vendorCode) || vendorCode.includes(code);
    });

    if (isVendor) {
        // Special case: "discount" is always treated as agency revenue even if in vendor list
        if (code.includes('discount')) {
            return true;
        }
        return false;
    }

    // Check if it's explicitly an agency item code
    const isAgency = AGENCY_ITEM_CODES.some(agencyItem => {
        const agencyCode = agencyItem.code.toLowerCase();
        return code.includes(agencyCode) || agencyCode.includes(code);
    });

    if (isAgency) {
        return true;
    }

    // Default to agency/vendor for unknown codes (conservative approach)
    return true;
};

// Helper function to get item category
export const getItemCodeCategory = (itemCode) => {
    const code = (itemCode || '').toLowerCase().trim();

    // Check agency items first
    const agencyItem = AGENCY_ITEM_CODES.find(item => {
        const itemCodeLower = item.code.toLowerCase();
        return code.includes(itemCodeLower) || itemCodeLower.includes(code);
    });

    if (agencyItem) {
        return agencyItem.category;
    }

    // Check vendor items
    const vendorItem = VENDOR_ITEM_CODES.find(item => {
        const itemCodeLower = item.code.toLowerCase();
        return code.includes(itemCodeLower) || itemCodeLower.includes(code);
    });

    if (vendorItem) {
        return vendorItem.category;
    }

    return 'General';
};

// Department Constants (mapping department IDs to names)
export const DEPARTMENT_MAP = {
    1: 'Technology',
    2: 'Creative',
    3: 'Media',
    4: 'Finance',
    5: 'Management',
    6: "Admin (don't use)",
    7: 'Social & Content',
    8: 'SEO',
    9: 'Data & Analytics',
    10: 'MarCom'
};

// Helper function to get department name by ID
export const getDepartmentName = (departmentId) => {
    const id = parseInt(departmentId);
    return DEPARTMENT_MAP[id] || '';
};
